import { getAgendaDays } from '../../../client/scripts/web-common/scripts/utils/DateHelper';
import { normalizeAgendaSettings } from '../../../client/scripts/web-common/scripts/utils/agendaHelpers';
import { queryInstanceToPostData } from '../views/Utils';
import { eventSelector, eventTimezoneSelector } from '../selectors/EventSelectors';
import {
    agendaSpeakerSelector,
    markerSelector,
    markersSelector,
    shouldFetchAgendaSettingsSelector,
    shouldFetchMarkersSelector,
    shouldFetchRegistrationEnabledSelector,
    agendaSpeakersSelector,
    settingsSelector
} from '../selectors/AgendaSelectors';

import {
    shouldFetchRegisteredSessionsSelector
} from '../selectors/MeSelectors';

import { queryInstanceSelector } from '../selectors/QuerySelectors';
import { throttle, omit } from 'underscore';
import { AGENDA_FILTERS } from '../constants/QueryReducer';
import { FILTERS_NAMES } from 'web-common/scripts/constants/Agenda';
import { personIdByTicketSelector } from '../selectors/UserProfilePageSelector';
import { fetchSessionsFromApi, fetchSessionsMapFromApi } from './helpers';

import {
    ADD_AGENDA_MARKER,
    CLIENT_NAVIGATION,
    IS_AGENDA_LOADING,
    REMOVE_AGENDA_MARKER,
    SELECT_AGENDA_FILTER_CAT,
    SET_AGENDA_SESSIONS,
    SET_AGENDA_SESSIONS_MAP,
    SET_AGENDA_SETTINGS,
    SET_AGENDA_SPEAKERS,
    UPDATE_AGENDA_SESSIONS,
    SET_MARKERS,
    TOGGLE_AGENDA_FILTER,
    SET_AGENDA_DAYS,
    SESSION_REGISTRATION_STATUS_CHANGED,
    REGISTERED_SESSION_IDS_FETCHED,
    CHANGE_ACTIVE_TAB,
    CHANGE_VIEW_MODE,
    SET_SLIDER_SESSIONS,
    SET_SCROLL_TO_SESSION_BROADCAST,
} from '../constants/ActionTypes';

import * as ApiUtils from '../views/reactViews/ApiUtil';
import { checkIfHasError } from '../views/reactViews/ApiUtil';

import { clearFilterGroup, setFilter, updateFilter, updateMultipleFiltersAction, clearFilterGroupAction } from './QueryActions';
import moment from 'moment';
import {isSessionEnded, isSessionStartsInMorethan} from '../views/reactViews/AgendaView/sessionBroadcastUtils';

const setLoadingAction = isLoading => ({
    type: IS_AGENDA_LOADING,
    isLoading
});

const setLoading = isLoading => dispatch => dispatch(setLoadingAction(isLoading));

const resetOffset = dispatch => updateFilter({ instanceId: AGENDA_FILTERS, key: 'offset', value: 0 })(dispatch);

export const clientNavigationAction = () => ({
    type: CLIENT_NAVIGATION
});

export const addMarker = marker => ({
    type: ADD_AGENDA_MARKER,
    payload: { marker }
});

export const setSessionsAction = sessions => ({ type: SET_AGENDA_SESSIONS, sessions });

export const setSessionsMapAction = sessionsMap => ({ type: SET_AGENDA_SESSIONS_MAP, sessionsMap });

export const updateSessionsAction = sessions => ({ type: UPDATE_AGENDA_SESSIONS, sessions });

export const removeMarker = marker => ({
    type: REMOVE_AGENDA_MARKER,
    payload: { marker }
});

export const setSessions = sessions => dispatch => dispatch(setSessionsAction(sessions));

const getSessions = async(dispatch, getState, updateURLQuery, query) => {
    try {
        const state = getState();
        const { id: eventId } = eventSelector(state);
        setLoading(true)(dispatch);
        const sessionsQuery = queryInstanceToPostData(state.queryReducer.instances.AGENDA_FILTERS);
        if (state.agendaReducer.activeTab === 'registrations') {
            sessionsQuery.registration = true;
        }
        const [sessions, sessionsMap] = await Promise.all([
            fetchSessionsFromApi(state)({
                query: sessionsQuery,
                eventId
            }),
            fetchSessionsMapFromApi(state)({
                eventId,
                query: omit(sessionsQuery, ['offset', 'count']),
            })
        ]);
        query.offset
            ? dispatch(updateSessionsAction(sessions))
            : dispatch(setSessions(sessions));
        dispatch(setSessionsMapAction(sessionsMap));
        updateURLQuery && updateURLQuery(query);
        setLoading(false)(dispatch);
    } catch (err) {
        setLoading(false)(dispatch);
    }
};

const fetchSessionsWithThrottle = throttle(getSessions, 1000);

const getQueryForAgendaFilter = state => {
    const queryInstance = queryInstanceSelector(state, { name: AGENDA_FILTERS });
    return queryInstance ? parseToQuery(queryInstance) : {};
};

export const fetchPartOfSession = () => async(dispatch, getState) => {
    setLoading(true)(dispatch);
    const query = getQueryForAgendaFilter(getState());
    query.offset = parseInt(query.offset || 0) + parseInt(query.count[0]);
    updateFilter({ instanceId: AGENDA_FILTERS, key: 'offset', value: query.offset })(dispatch);
    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch);
};

export const searchAgendaSessions = (search) => async(dispatch, getState) => {
    const { id: eventId } = eventSelector(getState());
    const state = getState();
    const agendaFilters = state.queryReducer.instances.AGENDA_FILTERS || [];
    return fetchSessionsFromApi(state)({
        query: {
            ...queryInstanceToPostData(agendaFilters),
            count: null,
            offset: 0,
            q: search
        }, eventId
    });
};

export const selectDay = (updateURLQuery, day) => (dispatch, getState) => {
    setLoading(true)(dispatch);
    resetOffset(dispatch);

    if (day === 'ALL') {
        clearFilterGroup({ instanceId: AGENDA_FILTERS, key: 'dates' })(dispatch);
    } else {
        updateFilter({ instanceId: AGENDA_FILTERS, key: 'dates', value: parseInt(day) })(dispatch);
    }

    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch, updateURLQuery);
};

export const toggleFilterAction = () => ({
    type: TOGGLE_AGENDA_FILTER
});


export const selectFilterCategoryAction = id => ({
    type: SELECT_AGENDA_FILTER_CAT,
    payload: { id }
});

const getQueryParamName = (FILTERS_NAMES, instanceKey) => FILTERS_NAMES.includes(instanceKey) ?
    instanceKey : 'filters';

export const parseToQuery = queryInstance => queryInstance.reduce((query, queryParam) => {
    const queryParamName = getQueryParamName(FILTERS_NAMES, queryParam.key);
    queryParam.value &&
        (query[queryParamName] = query.hasOwnProperty(queryParamName) ? [...query[queryParamName], queryParam.value] : [queryParam.value]);
    return query;
}, {});

const fetchSessions = (fetcher, getState, dispatch, updateURLQuery) => {
    const query = getQueryForAgendaFilter(getState());

    return fetcher(dispatch, getState, updateURLQuery, query);
};

export const toggleTag = (updateURLQuery, categoryName, tagId) => (dispatch, getState) => {
    setLoading(true)(dispatch);
    resetOffset(dispatch);
    setFilter({ instanceId: AGENDA_FILTERS, key: categoryName, value: tagId })(dispatch);
    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch, updateURLQuery);
};

export const clearCategoryTags = (updateURLQuery, categoryName) => (dispatch, getState) => {
    setLoading(true)(dispatch);
    resetOffset(dispatch);
    clearFilterGroup({ instanceId: AGENDA_FILTERS, key: categoryName })(dispatch);
    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch, updateURLQuery);
};

export const clearLocationsTags = (updateURLQuery) => (dispatch, getState) => {
    setLoading(true)(dispatch);
    resetOffset(dispatch);
    clearFilterGroup({ instanceId: AGENDA_FILTERS, key: 'locations' })(dispatch);
    clearFilterGroup({ instanceId: AGENDA_FILTERS, key: 'sublocations' })(dispatch);
    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch, updateURLQuery);
};

export const updateTime = timeRange => dispatch => {
    updateFilter({ instanceId: AGENDA_FILTERS, key: 'startTime', value: timeRange[0] * 60 })(dispatch);
    updateFilter({ instanceId: AGENDA_FILTERS, key: 'endTime', value: timeRange[1] * 60 })(dispatch);
};

export const selectTime = (updateUrlQuery, timeRange) => (dispatch, getState) => {
    setLoading(true)(dispatch);
    resetOffset(dispatch);
    updateTime(timeRange)(dispatch);
    return fetchSessions(getSessions, getState, dispatch, updateUrlQuery);
};

export const clearTime = updateURLQuery => (dispatch, getState) => {
    setLoading(true)(dispatch);
    resetOffset(dispatch);
    clearFilterGroup({ instanceId: AGENDA_FILTERS, key: 'startTime' })(dispatch);
    clearFilterGroup({ instanceId: AGENDA_FILTERS, key: 'endTime' })(dispatch);
    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch, updateURLQuery);
};

export const clearAllTags = updateURLQuery => (dispatch, getState) => {
    const queryInstance = queryInstanceSelector(getState(), { name: AGENDA_FILTERS });
    if (!queryInstance) {
        return;
    }
    setLoading(true)(dispatch);
    resetOffset(dispatch);
    queryInstance.forEach(entry => {
        !['offset', 'count'].includes(entry.key) && clearFilterGroup({ instanceId: AGENDA_FILTERS, key: entry.key })(dispatch);
    });

    return fetchSessions(fetchSessionsWithThrottle, getState, dispatch, updateURLQuery);
};

export const changeSessionRegistrationStatus = status => ({
    type: SESSION_REGISTRATION_STATUS_CHANGED,
    payload: status
});

export const registeredSessionIdsFetched = ids => ({
    type: REGISTERED_SESSION_IDS_FETCHED,
    payload: ids
});



export const toggleMarker = markerProps => async(dispatch, getState) => {
    try {
        const state = getState();
        const { id: eventId } = eventSelector(state);
        const allMarkers = markersSelector(state);
        const marker = markerSelector(allMarkers, markerProps);
        const method = marker ? 'DELETE' : 'POST';
        const options = {
            method,
            data: markerProps
        };
        const result = await ApiUtils.toggleMarker(options);

        const action = marker ? removeMarker : addMarker;
        dispatch(action(markerProps));
        return result;
    } catch (err) {
        throw err;
    }
};

const setMarkersAction = markers => ({ type: SET_MARKERS, markers });

export const fetchMarkers = () => async(dispatch, getState) => {
    const state = getState();
    if (shouldFetchMarkersSelector(state)){
        try {
            const markers = await ApiUtils.getMarkers();
            const verifiedMarkers = checkIfHasError(markers);
            dispatch(setMarkersAction(verifiedMarkers));
        } catch (err) {
            throw err;
        }
    }
};

export const setSettingsAction = settings => ({ type: SET_AGENDA_SETTINGS, settings });

export const setSpeakersAction = speakers => ({ type: SET_AGENDA_SPEAKERS, speakers });

export const setAgendaDaysAction = agendaDays => ({ type: SET_AGENDA_DAYS, agendaDays });

export const fetchAgendaSettings = eventId => async(dispatch, getState) => {
    try {
        const settings = await ApiUtils.getSettings(eventId);
        const verifiedSettings = checkIfHasError(settings);
        const normalizedSettings = normalizeAgendaSettings(verifiedSettings || {}, eventSelector(getState()));
        const { startDate, endDate } = normalizedSettings;
        const days = getAgendaDays(startDate, endDate);

        dispatch(setSettingsAction(normalizedSettings));
        dispatch(setAgendaDaysAction(days));
    } catch (err) {
        throw err;
    }
};

const fetchRegistrationEnabled = eventId => async dispatch => {
    const isSessionRegistrationEnabled = await ApiUtils.getRegistrationEnabled({ eventId });
    dispatch(changeSessionRegistrationStatus(isSessionRegistrationEnabled));
};

const fetchRegisteredSessions = eventId => async(dispatch, state) => {
    const {_, personId} = personIdByTicketSelector(state);
    const registeredSessionIds = await ApiUtils.getRegisteredSessionIds({ eventId, personId });
    dispatch(registeredSessionIdsFetched(registeredSessionIds));
};

const changeActiveTabAction = tab => ({
    type: CHANGE_ACTIVE_TAB,
    payload: tab
});


export const changeActiveTab = tab => async(dispatch, getState) => {
    dispatch(clearFilterGroupAction({instanceId: AGENDA_FILTERS, key: 'sessionsIds'}));
    dispatch(updateFilter({ instanceId: AGENDA_FILTERS, key: 'offset', value: 0 }));
    dispatch(changeActiveTabAction(tab));
};

export const fetchSpeakers = ({ eventId }) => async(dispatch) => {
    try {
        const speakers = await ApiUtils.getSpeakers({eventId});
        const verifiedSpeakers = checkIfHasError(speakers);
        dispatch(setSpeakersAction(verifiedSpeakers));
    } catch (err) {
        throw err;
    }
};

export const fetchSessionRegData = ({ eventId }) => async(dispatch, getState) => {
    const state = getState();
    shouldFetchRegistrationEnabledSelector(state) && await fetchRegistrationEnabled(eventId)(dispatch);
    shouldFetchRegisteredSessionsSelector(state) && await fetchRegisteredSessions(eventId)(dispatch, state);
};


export const fetchAgenda = ({ eventId }) => async(dispatch, getState) => {
    try {
        setLoading(true)(dispatch);
        const state = getState();
        const agendaFilters = state.queryReducer.instances.AGENDA_FILTERS || [];
        const [sessions, sessionsMap] = await Promise.all([
            fetchSessionsFromApi(state)({
                query: queryInstanceToPostData(agendaFilters),
                eventId
            }),
            fetchSessionsMapFromApi(state)({
                eventId,
                query: queryInstanceToPostData(agendaFilters, ['offset', 'count'])
            }),
            shouldFetchAgendaSettingsSelector(state) && await fetchAgendaSettings(eventId)(dispatch, getState),
        ]);

        const verifiedSessions = checkIfHasError(sessions);
        dispatch(setSessionsAction(verifiedSessions));
        dispatch(setSessionsMapAction(sessionsMap));
        const settings = settingsSelector(state);
        if (settings?.hideDatesWithNoSessions) {
            const sessionsDays = await ApiUtils.getSessionDays({eventId});
            dispatch(setAgendaDaysAction(sessionsDays));
        }

        setLoading(false)(dispatch);
    } catch (err) {
        setLoading(false)(dispatch);
        throw err;
    }
};

export const changeViewMode = tab => ({
    type: CHANGE_VIEW_MODE,
    payload: tab
});


export const getSessionsByIds = (ids, { registration } = {}) => async(dispatch, getState) => {
    const filters = ids.map(id => ({instanceId: AGENDA_FILTERS, key: 'sessionsIds', value: id}));
    dispatch(updateMultipleFiltersAction({filters}));
    const state = getState();
    const { id: eventId } = eventSelector(state);
    setLoading(true)(dispatch);
    const query = queryInstanceToPostData(state.queryReducer.instances.AGENDA_FILTERS);
    if (registration) {
        query.registration = true;
    }

    const [sessions, sessionsMap] = await Promise.all([
        fetchSessionsFromApi(state)({
            query,
            eventId
        }),
        fetchSessionsMapFromApi(state)({
            eventId,
            query
        })
    ]);
    setLoading(false)(dispatch);
    const verifiedSessions = checkIfHasError(sessions);
    dispatch(setSessionsAction(verifiedSessions));
    dispatch(setSessionsMapAction(sessionsMap));
};

const setSliderSessionsAction = (sessions) => ({ type: SET_SLIDER_SESSIONS, sessions });

export const getSliderSessions = (ids) => async(dispatch, getState) => {
    const state = getState();
    const { id: eventId } = eventSelector(state);
    const sessions = await fetchSessionsFromApi(state)({
        query: {
            sessionsIds: ids
        },
        eventId
    });
    const verifiedSessions = checkIfHasError(sessions);
    dispatch(setSliderSessionsAction(verifiedSessions));
};

const enrichSessionsWithSpeakerData = async({sessions, dispatch, state, eventId}) =>{
    let speakers = agendaSpeakersSelector(state);
    if (!speakers.length) {
        speakers = await ApiUtils.getSpeakers({eventId});
        const verifiedSpeakers = checkIfHasError(speakers);
        dispatch(setSpeakersAction(verifiedSpeakers));
    }
    const enrichedSessions = sessions || [];
    enrichedSessions?.forEach(session => {
        let fullSpeakers = [];
            session?.speakers?.forEach(({speakerId}) => {
                const speaker = agendaSpeakerSelector({ agendaReducer: { speakers }}, {eventId, speakerId});
                if (speaker){
                    fullSpeakers.push(speaker);
                }
            });
            session.speakers = fullSpeakers;
    });
    return enrichedSessions || [];
};

export const getUpcomingAndLiveSessions =(upcomingMinutes = 60, sessionLimit = 60, interval = true, intervalMinutes = 5)=> async(dispatch, getState) => {
    try {
        const state = getState();
        const oneMinute = 60 * 1000;
        const {id: eventId} = eventSelector(state);
        const eventTimeZone = eventTimezoneSelector(state);
        const now = moment().tz(eventTimeZone).valueOf();
        let startDateTime;
        if (interval) {
            startDateTime = now - (now % (intervalMinutes * oneMinute));
        } else {
            startDateTime = now;
        }
        const endDateTime = startDateTime +(upcomingMinutes * oneMinute);
        const query = {
            count: sessionLimit,
            startDateTime,
            offset: 0,
            endDateTime
        };
        const sessions = await fetchSessionsFromApi(state)({
            eventId,
            query
        });
        const upcomingAndLiveVirtualSessions = sessions?.sessions?.filter(session => {
            const isSessionOver = isSessionEnded(session, eventTimeZone);
            const isVirtualSession = session.enableVirtualSession;
            return (isVirtualSession && !isSessionOver);
        });
        const enrichedUpcomingAndLiveVirtualSessions = await enrichSessionsWithSpeakerData({sessions: upcomingAndLiveVirtualSessions, dispatch, state, eventId});
        return enrichedUpcomingAndLiveVirtualSessions || [];
    } catch (e){
        return [];
    }
};

export const setScrollToSessionBroadcastAction = (scrollToBroadcast) => ({
    type: SET_SCROLL_TO_SESSION_BROADCAST,
    scrollToBroadcast
});
